import _ from 'lodash';
import {
  sqDurationStore,
  sqTreemapStore,
  sqTrendConditionStore,
  sqTrendScalarStore,
  sqTrendSeriesStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { getCapsuleFormula, getDisplayRangeFromUrlParams } from '@/datetime/dateTime.utilities';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { priorityColors } from '@/services/systemConfiguration.utilities';
import { findItemIn } from '@/trend/trendDataHelper.utilities';
import { catchItemDataFailure } from '@/trendData/trend.actions';
import { setConditionDataStatusLoading, setConditionDataStatusPresent } from '@/trendData/trendCondition.actions';
import { errorToast } from '@/utilities/toast.utilities';
import { encodeParameters, getShortIdentifier } from '@/utilities/utilities';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { sqTreesApi } from '@seeqdev/api/endpoint/TreesApi';
import type { PushOption } from '@/core/flux.service';
import type { CapsuleSelectionValue } from '@/reportEditor/report.constants';
import type { TreemapItem } from '@/treemap/TreemapChart.atom';
import type { TreemapItemOutputV1 } from '@seeqdev/api/model/TreemapItemOutputV1';

type TreemapCondition = {
  id?: string | number;
  color: string;
};

/**
 * Sets the parent asset for swapping, the children underneath it will be displayed in the treemap.
 *
 * @param {Object} asset - The parent asset
 * @param {String} asset.id - The ID of the asset
 * @param {String} [option] - A push constant, such as PUSH_IGNORE
 *
 * @return {Promise<void>} - Resolved when the treemap is fetched
 */
export function setParent(asset: { id: string } | undefined, option?: PushOption): Promise<void> {
  flux.dispatch('TREEMAP_SET_PARENT', _.pick(asset, ['id']), option);

  return fetchTreemap();
}

interface SetStatisticPayload {
  /** The id of the signal to use for calculating the statistic */
  id?: string | number | CapsuleSelectionValue;
  /** One of the statistic keys from SAMPLE_FROM_SCALARS.VALUE_METHODS */
  key: string | null;
}

/**
 * Set a statistic to be displayed on each asset node.
 *
 * @param {Number} index - Which statistic is being set
 * @param {SetStatisticPayload} payload - Will include the id and the key props
 *
 * @return {Promise<void>} - Resolved when the treemap is fetched
 */
export function setStatistic(index: number, payload: SetStatisticPayload): Promise<void> {
  flux.dispatch('TREEMAP_SET_STATISTIC', { ...payload, index });

  return fetchTreemap();
}

/**
 * Sets or clears the Treemap-only color override for a worksheet Treemap condition.
 *
 * @param id - The condition id.
 * @param color - The override color, or undefined to clear the override.
 * @return {Promise<void>} - Resolved when the treemap is fetched.
 */
export function setTreemapConditionColor(id: string | number, color: string | undefined): Promise<void> {
  flux.dispatch('TREEMAP_SET_CONDITION_COLOR', { id: String(id), color });

  return fetchTreemap();
}

/**
 * Updates the number of pixels of the chart so that the auto-update interval calculates correctly.
 *
 * @param {Number} width - The width, in pixels, of the chart
 */
export function setDisplayPixels(width: number): void {
  // A rough estimate of what the width on the trend chart is, just so that the auto-update calculation is roughly
  // the same between views
  const Y_AXIS_WIDTH = 40;
  flux.dispatch('AUTO_UPDATE_SET_DISPLAY_PIXELS', { displayPixels: width + Y_AXIS_WIDTH }, PUSH_IGNORE);
}

/**
 * Fetches the treemap based on the configuration settings.
 */
export function fetchTreemap(): Promise<void> {
  const conditions = sqTrendConditionStore.items;
  const displayRange = headlessRenderMode()
    ? (getDisplayRangeFromUrlParams() ?? sqDurationStore.displayRange)
    : sqDurationStore.displayRange;
  const signalValueMethods = _.filter(
    SAMPLE_FROM_SCALARS.VALUE_METHODS,
    _.flow(_.property('input'), _.partial(_.includes, _, 'sample')),
  );
  if (sqWorksheetStore.view.key !== WORKSHEET_VIEW.TREEMAP) {
    return Promise.resolve();
  }

  if (_.isEmpty(sqTreemapStore.priorityColors)) {
    const colors = _.chain(priorityColors())
      .filter((priority) => priority.level >= 0)
      .map('color')
      .tap((colors: string[]) => {
        const neutralColor = _.toLower(_.last(colors));
        // R21 moved to white as the neutral color, but treemap should still use green unless otherwise specified
        colors[colors.length - 1] = neutralColor === '#ffffff' || neutralColor === 'unset' ? '#068C45' : neutralColor;
      })
      .value();
    flux.dispatch('TREEMAP_SET_COLORS', { colors });
  }

  if (!conditions.length) {
    flux.dispatch('TREEMAP_SET_HELP_KEY', { helpKey: 'addCondition' });
    return Promise.resolve();
  }

  if (!allUseSameAsset(conditions)) {
    flux.dispatch('TREEMAP_SET_HELP_KEY', { helpKey: 'mustUseSameAsset' });
    return Promise.resolve();
  }

  if (!_.every(conditions, hasPriorityColor)) {
    flux.dispatch('TREEMAP_SET_HELP_KEY', { helpKey: 'selectPriorities' });
    return Promise.resolve();
  }

  flux.dispatch('TREEMAP_SET_SWAP', _.pick(conditions[0].assets[0], ['id', 'name']));

  if (_.isEmpty(sqTreemapStore.parent)) {
    return sqTreesApi
      .getTree({ id: sqTreemapStore.swap.id!, offset: 0, limit: 100 })
      .then(({ data: { item } }) => setParent(_.last(item?.ancestors)!, PUSH_IGNORE));
  }

  if (_.isEmpty(sqTreemapStore.breadcrumbs)) {
    sqTreesApi.getTree({ id: sqTreemapStore.parent.id!, offset: 0, limit: 100 }).then(({ data: { item } }) =>
      flux.dispatch('TREEMAP_SET_BREADCRUMBS', {
        breadcrumbs: item?.ancestors?.concat([item]).map(({ id, name }) => ({ id, name })),
      }),
    );
  }

  const conditionIds = _.chain(conditions)
    .sortBy((item) => _.indexOf(sqTreemapStore.priorityColors, getTreemapConditionColor(item)))
    .map('id')
    .value();
  const parameters: Record<string, string | number | undefined> = {};
  const formulas = _.map(sqTreemapStore.validStatistics, function (statistic, i) {
    const identifier = getShortIdentifier(i);
    // for now we do not support providing a unit from within the treemap UI so we filter out the parameter to ensure
    // the formulas using a $unit will still compile as expected (the Backend code provides seconds as the default
    // unit)
    const stat = _.find(signalValueMethods, ['key', statistic.key])!['stat'].replace('$unit', '');
    const capsule = getCapsuleFormula(displayRange);
    parameters[identifier] = statistic.id;

    return `$${identifier}.aggregate(${stat}, ${capsule})`;
  });

  const options = {
    start: displayRange.start.toISOString(),
    end: displayRange.end.toISOString(),
    swapId: sqTreemapStore.swap.id!,
    parentId: sqTreemapStore.parent.id!,
    conditionIds,
    parameters: encodeParameters(parameters),
    formulas,
  };
  const cancellationGroup = 'treemap';

  _.forEach(options.conditionIds, (id) => setConditionDataStatusLoading(id));
  cancelGroup(cancellationGroup);

  return sqTreesApi
    .treemap(options, { cancellationGroup })
    .then(({ data, headers }) => {
      const tree: TreemapItem[] = (data.tree || []).map((node: TreemapItemOutputV1) => {
        const color =
          node.priority === -1
            ? sqTreemapStore.neutralColor!
            : getTreemapConditionColor(_.find(conditions, ['id', options.conditionIds[node.priority! as number]])!);
        const displayScalars = _.chain(node.displayScalars)
          .map((scalar, i) =>
            _.assign(scalar, {
              statistic: sqTreemapStore.validStatistics[i],
            }),
          )
          .filter('statistic') // in case it has been removed since starting fetch
          .map((scalar) => {
            const item = findItemIn([sqTrendSeriesStore, sqTrendScalarStore], scalar.statistic.id!);
            return {
              value: scalar.value,
              uom: scalar.uom ?? '',
              title: _.find(signalValueMethods, ['key', scalar.statistic.key])!['title'],
              signal: item.name,
              formatOptions: item.formatOptions,
            };
          })
          .value();

        return _.assign(node, { color, displayScalars, asset: _.pick(node.asset, ['id', 'name']) });
      });

      flux.dispatch('TREEMAP_SET_TREE', { tree });
      _.forEach(options.conditionIds, (id) => {
        setConditionDataStatusPresent({
          id,
          warningCount: data.warningCount,
          warningLogs: data.warningLogs,
          timingInformation: headers['server-timing'],
          meterInformation: headers['server-meters'],
          isSharedRequest: true,
        });
      });
    })
    .catch((response) => {
      errorToast({ httpResponseOrError: response });
      _.forEach(options.conditionIds, (id) => catchItemDataFailure(id, cancellationGroup, response));
    });
}

/**
 * Determines if a condition is using a priority color.
 *
 * @param {Object} item - The item to check.
 *
 * @return {boolean} True if it is using a priority color, false otherwise
 */
export function hasPriorityColor(item: TreemapCondition): boolean {
  return _.includes(sqTreemapStore.priorityColors, getTreemapConditionColor(item));
}

/**
 * Returns the effective Treemap color for a condition, preferring a Treemap-specific override.
 *
 * @param item - The condition to resolve.
 * @return The effective condition color.
 */
function getTreemapConditionColor(item: TreemapCondition): string {
  const id = _.isNil(item.id) ? undefined : String(item.id);
  return (id ? sqTreemapStore.conditionColors[id] : undefined) ?? item.color;
}

interface AssetBasedItem {
  assets?: { id?: string }[];
}

/**
 * Determines if all items are asset-based and from the same asset.
 *
 * @param {AssetBasedItem[]} items - The array of items to check.
 *
 * @return {boolean} True if all items are asset-based and from the same asset, false otherwise
 */
export function allUseSameAsset(items: AssetBasedItem[]): boolean {
  return (
    _.every(items, 'assets.length') && _.chain(items).flatMap('assets').compact().uniqBy('id').value().length === 1
  );
}

export function setProportionBySignal(proportionBy: string | null) {
  flux.dispatch('TREEMAP_SET_SIGNAL_TO_PROPORTION_BY', { proportionBy });
}

export function setProportionByColumn(proportionBy: string | null) {
  flux.dispatch('TREEMAP_SET_COLUMN_TO_PROPORTION_BY', { proportionBy });
}

export function setIsUniformSizing(isUniformSizing: boolean) {
  flux.dispatch('TREEMAP_SET_IS_UNIFORM_SIZING', { isUniformSizing });
}

export function setTreemapSquareScalingInverse(inverse: boolean) {
  flux.dispatch('TREEMAP_SET_SQUARE_SCALING_INVERSE', { inverse });
}
